import { useCallback, useEffect, useRef, useState, type ChangeEvent, type RefObject } from 'react'
import { toast } from 'sonner'
import { ErrorBoundary } from '@/app/ErrorBoundary'
import { validateDiagnosticsConfiguration } from '@/app/diagnostics/diagnostics'
import { EmptyState } from '@/app/layout/EmptyState'
import { DesktopLayout } from '@/app/layout/DesktopLayout'
import { MobileLayout } from '@/app/layout/MobileLayout'
import { validateSettingsConfiguration } from '@/app/settings/settings'
import { useBackButtonUndo } from '@/app/backButton'
import { usePwaUpdates } from '@/app/pwa'
import { handleSharedInboxLaunch } from '@/app/shareInbox'
import { useApplyTheme } from '@/app/theme'
import { validateOutputPresets } from '@/content/instagram'
import { validateHelpContent } from '@/content/help'
import { validateFilmFormats } from '@/content/instantFilm'
import { validateCollageColorPresets, validateCollageTemplates, validateGradientPresets } from '@/content/collageTemplates'
import { validateContact } from '@/content/contact'
import { validateSite } from '@/content/site'
import { validateWhatsNew } from '@/content/whatsNew'
import { validateBuildIdentity } from '@/app/buildIdentity'
import {
  IMAGE_FILE_ACCEPT,
  loadSource as decodeSource,
  SourceImageNotice,
  validateFormatSupport,
} from '@/core/image'
import {
  NodeKind,
  containsNodeKind,
  validateAdjustmentConfiguration,
} from '@/core/document'
import { validateEstimatedBytesPerPixel } from '@/core/export'
import { ReportingStage, surfaceError } from '@/core/reporting'
import {
  listRecentEdits,
  persistNewEdit,
  PersistenceStatus,
  restoreRecentEdit,
  startAutosave,
} from '@/core/persistence'
import { useEditSession } from '@/core/store/editSession'
import { validateBottomSheetConfiguration } from '@/core/store/uiState'
import { useIsCoarsePointer } from '@/lib/usePointerType'
import { validateTestIds } from '@/lib/testIds'
import { validateStorageKeys } from '@/lib/storage'
import { validateShareTargetConfiguration } from '@/core/shareTarget'
import { useUndoRedoShortcuts } from '@/lib/useUndoRedoShortcuts'
import { validateToolRegistry } from '@/tools/registry'
import { fillSelectedCollageFiles } from '@/tools/collage/actions'
import { Toaster } from '@/ui/shadcn/sonner'
import { WhatsNewNotice } from '@/app/whatsNew/WhatsNewDialog'
import { validateIllustrationPalette } from '@/ui/illustrations/palette'

validateOutputPresets()
validateHelpContent()
validateFilmFormats()
validateCollageTemplates()
validateGradientPresets()
validateCollageColorPresets()
validateContact()
validateSite()
validateBuildIdentity()
validateDiagnosticsConfiguration()
validateEstimatedBytesPerPixel()
validateFormatSupport()
validateAdjustmentConfiguration()
validateShareTargetConfiguration()
validateToolRegistry()
validateTestIds()
validateBottomSheetConfiguration()
validateSettingsConfiguration()
validateStorageKeys()
validateWhatsNew()
validateIllustrationPalette()

interface EditorSurfaceProps {
  inputRef: RefObject<HTMLInputElement | null>
  isCoarse: boolean
  restoreError: Error | null
  chooseImage: () => void
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void
  processFile: (file: File) => void
}

function EditorSurface({
  inputRef,
  isCoarse,
  restoreError,
  chooseImage,
  onInputChange,
  processFile,
}: EditorSurfaceProps) {
  const document = useEditSession((state) => state.document)
  if (restoreError) throw restoreError

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        accept={IMAGE_FILE_ACCEPT}
        onChange={onInputChange}
        aria-label="Choose an image"
      />
      {!document
        ? <EmptyState mobile={isCoarse} onChooseImage={chooseImage} onFile={processFile} />
        : isCoarse
          ? <MobileLayout onChooseImage={chooseImage} />
          : <DesktopLayout onChooseImage={chooseImage} />}
    </>
  )
}

function EditorApp() {
  const inputRef = useRef<HTMLInputElement>(null)
  const sources = useEditSession((state) => state.sources)
  const isCoarse = useIsCoarsePointer()
  const setHandlesVisible = useEditSession((state) => state.setHandlesVisible)
  const configuredPointer = useRef(false)
  const restoreStarted = useRef(false)
  const firstFrameNoticeShown = useRef(false)
  const [restoreError, setRestoreError] = useState<Error | null>(null)
  const [launchReady, setLaunchReady] = useState(false)

  useApplyTheme()
  usePwaUpdates()
  useBackButtonUndo()
  useUndoRedoShortcuts()

  useEffect(() => {
    if (firstFrameNoticeShown.current) return
    if (![...sources.values()].some((source) => source.notice === SourceImageNotice.FirstFrame)) return
    firstFrameNoticeShown.current = true
    toast.info('Animated image — using the first frame')
  }, [sources])

  useEffect(() => startAutosave((error) => {
    surfaceError(error, 'Could not autosave this edit.', { stage: ReportingStage.Persistence })
  }), [])

  useEffect(() => {
    if (restoreStarted.current) return
    restoreStarted.current = true
    void (async () => {
      try {
        if (await handleSharedInboxLaunch()) return
        const records = await listRecentEdits()
        const blockedEditId = useEditSession.getState().recoveryBlockedEditId
        const openRecord = records.find((record) => record.isOpen && record.id !== blockedEditId)
        if (openRecord) {
          try {
            await restoreRecentEdit(openRecord.id)
          } catch (error) {
            useEditSession.getState().setRecoveryBlockedEditId(openRecord.id)
            throw error
          }
        }
        setLaunchReady(true)
      } catch (error) {
        useEditSession.getState().setPersistenceStatus(PersistenceStatus.Disabled)
        setRestoreError(error instanceof Error
          ? error
          : new Error('Could not restore the open edit.', { cause: error }))
      }
    })()
  }, [])

  useEffect(() => {
    if (!configuredPointer.current) {
      setHandlesVisible(!isCoarse)
      configuredPointer.current = true
    }
  }, [isCoarse, setHandlesVisible])

  const processFile = useCallback(async (file: File) => {
    const state = useEditSession.getState()
    state.setIsLoading(true)
    try {
      const decoded = await decodeSource(file)
      useEditSession.getState().loadSource(decoded)
      try {
        await persistNewEdit()
      } catch (error) {
        surfaceError(error, 'Could not save this edit in the browser.', { stage: ReportingStage.Persistence })
      }
    } catch (error) {
      surfaceError(error, 'Could not load the selected image', { stage: ReportingStage.SourceLoad })
    } finally {
      useEditSession.getState().setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const files = [...(event.clipboardData?.files ?? [])]
      if (files.length === 0) return
      const state = useEditSession.getState()
      if (state.document && containsNodeKind(state.document.root, NodeKind.Collage)) {
        void fillSelectedCollageFiles(files).catch((error: unknown) => {
          surfaceError(error, 'Could not paste photos into the collage.', { stage: ReportingStage.SourceLoad })
        })
        return
      }
      const file = files[0]
      if (!file) throw new Error('Pasted file collection is unexpectedly empty')
      void processFile(file)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [processFile])

  const chooseImage = () => {
    const input = inputRef.current
    if (!input) throw new Error('Image file picker is not configured')
    input.click()
  }

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file) void processFile(file)
  }

  return (
    <>
      <ErrorBoundary onRecovered={() => setRestoreError(null)}>
        <EditorSurface
          inputRef={inputRef}
          isCoarse={isCoarse}
          restoreError={restoreError}
          chooseImage={chooseImage}
          onInputChange={onInputChange}
          processFile={(file) => void processFile(file)}
        />
      </ErrorBoundary>
      {launchReady ? <WhatsNewNotice mobile={isCoarse} /> : null}
      <Toaster />
    </>
  )
}

export function App() {
  return <EditorApp />
}
